import * as React               from "react";

import "./ai-text-generation-tools-2.scss";

import {ComponentLink,
        Link,
        makeChain}      from "@Common/react/cst2";
import {UseState}       from "@Common/react/use-state";
import {consumeContext} from "@Common/react/context";
import {Load2}          from "../../react/load2";
import {fragments}      from "@Common/react/fragments";
import {Effect}         from "@Common/react/effect";

import {AiErrorModal}            from "./ai-error-modal";
import {AiToolOption}            from "./ai-tool-option";
import {AutoTextGenerationModal} from "./auto-text-generation-modal";
import {AiTextFromImageModal}    from "./ai-text-from-image-modal";
import {isDevXd}                 from "@Common/ui-components/debug/is-dev";
import {AiToolInteractionTrigger} from "./ai-tool-interaction-trigger";


export const AiTextGenerationTools2 = ({
    idOrder,
    idField,
    currFieldVal,
    setFieldVal,
    displayLoading,
    fieldMaxChars,
    overrideMaxChars       = false,
    textGenerationModal,
    aiTextFromImageModal,
    orderImages,
}:{
    idOrder                : number,
    idField                : string,
    currFieldVal           : string,
    setFieldVal            : (value:string) => void,
    displayLoading         : (value:boolean) => void,
    fieldMaxChars          : number,
    overrideMaxChars      ?: boolean,
    textGenerationModal    : ComponentLink<typeof AutoTextGenerationModal>,
    aiTextFromImageModal  ?: ComponentLink<typeof AiTextFromImageModal>,
    orderImages            : ComponentLink<typeof Load2>,
}) => (

    makeChain<JSX.Element>()

    .then(...consumeContext("apis"))

    .then(...consumeContext("session"))

    .then(...consumeContext("company"))

    .then(...consumeContext("userLevels"))

    .then("expandAiTools", (_, next:ComponentLink<typeof UseState<boolean>>) => UseState({initValue: false, next}))

    .then("drawerShouldExpandUp", (_, next:ComponentLink<typeof UseState<boolean>>) => UseState({initValue: false, next}))

    .then("rewordFunc", ({apis, session, company}, next:Link<JSX.Element, () => Promise<boolean>>) => next(async () => {
        displayLoading(true);
        let timeout, failureValue="ERROR: timeout", timeLimit=26000;
        const timeoutPromise = new Promise((resolve, reject) => {
            timeout = setTimeout(() => {
                resolve(failureValue);
            }, timeLimit);
        });
        let task = apis.next.gpt.aiReword({"userCue": currFieldVal, "maxChars": fieldMaxChars, "maxCharOverride": overrideMaxChars});
        let rewordedText = await Promise.race([task, timeoutPromise]);
        AiToolInteractionTrigger();
        if(rewordedText.error || typeof rewordedText == "string") {
            await apis.next.gpt.logAiInteraction({
                "toolUsed": "Reword Text",
                "idOrder": idOrder,
                "userDetails": {"idCompany": company.idCompany, "idBranch": session.data.agent.idBranch, "idAgent": session.data.agent.idAgent},
                "userCue": currFieldVal,
                "gptResponse": rewordedText.error?.message ? rewordedText.error.message : "timeout",
                "responseCode": rewordedText.error?.type ? rewordedText.error.type :"timeout"
            });
            displayLoading(false);
            return false;
        } else {
            await apis.next.gpt.logAiInteraction({
                "toolUsed": "Reword Text",
                "idOrder": idOrder,
                "userDetails": {"idCompany": company.idCompany, "idBranch": session.data.agent.idBranch, "idAgent": session.data.agent.idAgent},
                "model": rewordedText.requestParams.model,
                "fullPrompt": rewordedText.requestParams.messages,
                "userCue": currFieldVal,
                "gptResponse": rewordedText['choices'][0]['message']['content'],
                "responseCode": "stop",
                "promptTokens": rewordedText['usage']['prompt_tokens'],
                "completionTokens": rewordedText['usage']['completion_tokens']
            });
            setFieldVal(rewordedText['choices'][0]['message']['content']);
            displayLoading(false);
            return true;
        }
    }))

    .then("RewordModal", ({rewordFunc}, next:ComponentLink<typeof AiErrorModal>) => AiErrorModal({
        regen: async () => {
            return rewordFunc();
        },
        next
    }))

    .then("summarizeFunc", ({apis, session, company}, next:Link<JSX.Element, () => Promise<boolean>>) => next(async () => {
        displayLoading(true);
        let timeout, failureValue="ERROR: timeout", timeLimit=26000;
        const timeoutPromise = new Promise((resolve, reject) => {
            timeout = setTimeout(() => {
                resolve(failureValue);
            }, timeLimit);
        });
        let task = apis.next.gpt.aiSummarize({"userCue": currFieldVal, "maxChars": fieldMaxChars, "maxCharOverride": overrideMaxChars});
        let summarizedText = await Promise.race([task, timeoutPromise]);
        AiToolInteractionTrigger();
        if(summarizedText.error || typeof summarizedText == "string") {
            await apis.next.gpt.logAiInteraction({
                "toolUsed": "Summarize Text",
                "idOrder": idOrder,
                "userDetails": {"idCompany": company.idCompany, "idBranch": session.data.agent.idBranch, "idAgent": session.data.agent.idAgent},
                "userCue": currFieldVal,
                "gptResponse": summarizedText.error?.message ? summarizedText.error.message : "timeout",
                "responseCode": summarizedText.error?.type ? summarizedText.error.type :"timeout"
            });
            displayLoading(false);
            return false;
        } else {
            await apis.next.gpt.logAiInteraction({
                "toolUsed": "Summarize Text",
                "idOrder": idOrder,
                "userDetails": {"idCompany": company.idCompany, "idBranch": session.data.agent.idBranch, "idAgent": session.data.agent.idAgent},
                "model": summarizedText.requestParams.model,
                "fullPrompt": summarizedText.requestParams.messages,
                "userCue": currFieldVal,
                "gptResponse": summarizedText['choices'][0]['message']['content'],
                "responseCode": "stop",
                "promptTokens": summarizedText['usage']['prompt_tokens'],
                "completionTokens": summarizedText['usage']['completion_tokens']
            });
            setFieldVal(summarizedText['choices'][0]['message']['content']);
            displayLoading(false);
            return true;
        }
    }))

    .then("SummarizeModal", ({summarizeFunc}, next:ComponentLink<typeof AiErrorModal>) => AiErrorModal({
        regen: async () => {
            return summarizeFunc();
        },
        next
    }))

    .then("expandFunc", ({apis, session, company}, next:Link<JSX.Element, () => Promise<boolean>>) => next(async () => {
        displayLoading(true);
        let timeout, failureValue="ERROR: timeout", timeLimit=26000;
        const timeoutPromise = new Promise((resolve, reject) => {
            timeout = setTimeout(() => {
                resolve(failureValue);
            }, timeLimit);
        });
        let task = apis.next.gpt.aiExpand({"userCue": currFieldVal, "maxChars": fieldMaxChars, "maxCharOverride": overrideMaxChars});
        let expandedText = await Promise.race([task, timeoutPromise]);
        AiToolInteractionTrigger();
        if(expandedText.error || typeof expandedText == "string") {
            await apis.next.gpt.logAiInteraction({
                "toolUsed": "Expand Text",
                "idOrder": idOrder,
                "userDetails": {"idCompany": company.idCompany, "idBranch": session.data.agent.idBranch, "idAgent": session.data.agent.idAgent},
                "userCue": currFieldVal,
                "gptResponse": expandedText.error?.message ? expandedText.error.message : "timeout",
                "responseCode": expandedText.error?.type ? expandedText.error.type :"timeout"
            });
            displayLoading(false);
            return false;
        } else {
            await apis.next.gpt.logAiInteraction({
                "toolUsed": "Expand Text",
                "idOrder": idOrder,
                "userDetails": {"idCompany": company.idCompany, "idBranch": session.data.agent.idBranch, "idAgent": session.data.agent.idAgent},
                "model": expandedText.requestParams.model,
                "fullPrompt": expandedText.requestParams.messages,
                "userCue": currFieldVal,
                "gptResponse": expandedText['choices'][0]['message']['content'],
                "responseCode": "stop",
                "promptTokens": expandedText['usage']['prompt_tokens'],
                "completionTokens": expandedText['usage']['completion_tokens']
            });
            setFieldVal(expandedText['choices'][0]['message']['content']);
            displayLoading(false);
            return true;
        }
    }))

    .then("ExpandModal", ({expandFunc}, next:ComponentLink<typeof AiErrorModal>) => AiErrorModal({
        regen: async () => {
            return expandFunc();
        },
        next
    }))

    .then("idAgent", ({session}, next:Link<JSX.Element, string>) => next(session?.value?.agentId))

    .then("hasCurrentXdPlusSubscription", ({session}, next:Link<JSX.Element, boolean>) => next(session?.data?.agent?.subscriptions?.some(subscription => subscription.subscribed)))

    // Scroll listener to determine when there is not enough space to expand the AI Tool drawer downwards(default)
    .then(({expandAiTools, drawerShouldExpandUp}, next:ComponentLink<typeof fragments>) => fragments(
        next(),
        Effect({
            action: () => {
                const listener = () => {
                    const fieldOptionsFrame = window.document.getElementById("field-options-ui-" + idField);
                    const fieldOptionsBottom = fieldOptionsFrame?.getBoundingClientRect().bottom;
                    const aiToolsDrawer = window.document.getElementById("ai-tools-drawer-" + idField);
                    // Sometimes aiToolsDrawer isn't rendered yet, so we can't calculate the height of it
                    // There may be a better way than hardcoding 205, but it has eluded me for now
                    const aiToolsDrawerHeight = aiToolsDrawer?.offsetHeight;
                    if (fieldOptionsBottom > window.innerHeight - 200) {
                        drawerShouldExpandUp.onChange(true);
                    } else {
                        drawerShouldExpandUp.onChange(false);
                    }
                    if (aiToolsDrawer?.getBoundingClientRect().bottom > window.innerHeight) {
                        expandAiTools.onChange(false);
                    }
                };
                window.addEventListener("scroll", listener);
                return () => window.removeEventListener("scroll", listener);

            }
        })
    ))

    .then(({expandAiTools, rewordFunc, RewordModal, summarizeFunc, SummarizeModal, expandFunc, ExpandModal, drawerShouldExpandUp, hasCurrentXdPlusSubscription}) => (<>
        <div className="ai-text-generation-tools-jh6"
            tabIndex={1}
            onBlur={() => expandAiTools.onChange(false)}
        >
            <div className="ai-tools-button" xd-qa-type="button" xd-qa-name="Text Gen Tools Button AI"
                onClick={() => {
                    expandAiTools.onChange(!expandAiTools.value);
                }}
            >
                <div className="icon"/>
                <p>AI Tools</p>
            </div>
            {expandAiTools.value && <>
                <div className="drawer-background" onClick={() => expandAiTools.onChange(false)}/>
                <div className={drawerShouldExpandUp.value ? "drawer expand-up" : "drawer"} id={"ai-tools-drawer-" + idField}>
                    <AiToolOption
                        name="expand"
                        active={currFieldVal.length >= 27 && currFieldVal.length / fieldMaxChars <= .8}
                        inactiveMessage={currFieldVal.length < 27 ? "Not enough text to expand" : currFieldVal.length / fieldMaxChars > .8 ? "Not enough space left to expand" : null}
                        onSelect={async () => {
                            expandAiTools.onChange(false);
                            if(!await expandFunc()) {
                                ExpandModal.show();
                            }
                        }}
                    />
                    <AiToolOption
                        name="summarize"
                        active={currFieldVal.length >= 67}
                        inactiveMessage={currFieldVal.length < 67 && "Not enough text to summarize"}
                        onSelect={async () => {
                            expandAiTools.onChange(false);
                            if(!await summarizeFunc()) {
                                SummarizeModal.show();
                            }
                        }}
                    />
                    <AiToolOption
                        name="reword"
                        active={currFieldVal.length >= 27}
                        inactiveMessage={currFieldVal.length < 27 && "Not enough text to reword"}
                        onSelect={async () => {
                            expandAiTools.onChange(false);
                            if(!await rewordFunc()) {
                                RewordModal.show();
                            }
                        }}
                    />
                    <AiToolOption
                        name="generate"
                        active={true}
                        onSelect={() => {
                            expandAiTools.onChange(false);
                            textGenerationModal.show();
                        }}
                    />
                    {hasCurrentXdPlusSubscription ?
                        <AiToolOption
                            name="image"
                            active={true}
                            onSelect={() => {
                                expandAiTools.onChange(false);
                                orderImages.reload();
                                aiTextFromImageModal.show();
                            }}
                        />
                    : null}
                </div>
            </>}
        </div>
    </>))

    .run()

);